import { zodResolver } from '@hookform/resolvers/zod';
import { Search, SortAsc } from 'lucide-react';
import { ElementRef, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useDebounce } from '@/hooks/useDebounce';
import { MerchandisingCollectionsQueryFilterDto } from '@/services/MerchandisingCollectionsService';

const formSchema = z.object({
  name: z.string(),
  order: z.enum([
    'createdAtAsc',
    'createdAtDesc',
    'updatedAtAsc',
    'updatedAtDesc',
  ]),
});

type FormData = z.infer<typeof formSchema>;

interface SearchFiltersProps {
  queryFiltersInitialState: Partial<MerchandisingCollectionsQueryFilterDto>;
  onQueryFilters: (
    queryFilters: Partial<MerchandisingCollectionsQueryFilterDto>,
  ) => void;
}

export function SearchFilters({
  queryFiltersInitialState,
  onQueryFilters,
}: SearchFiltersProps) {
  const submitRef = useRef<ElementRef<'button'>>(null);

  const { control, watch, register, handleSubmit, reset } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      order: 'createdAtAsc',
    },
  });

  const order = watch('order');
  const name = watch('name');
  const debouncedName = useDebounce(name, 300);

  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.MerchandisingCollections.components.SearchFilters',
  });

  function handleClearFilters() {
    onQueryFilters(queryFiltersInitialState);
    reset({ name: '', order: 'createdAtAsc' });
  }

  const onSubmit = handleSubmit((data) => {
    const orderBy: MerchandisingCollectionsQueryFilterDto['orderBy'] =
      data.order === 'createdAtAsc' || data.order === 'createdAtDesc'
        ? 'createdAt'
        : data.order === 'updatedAtAsc' || data.order === 'updatedAtDesc'
          ? 'updatedAt'
          : undefined;

    const orderDirection: MerchandisingCollectionsQueryFilterDto['orderDirection'] =
      data.order === 'createdAtAsc' || data.order === 'updatedAtAsc'
        ? 'ASC'
        : data.order === 'createdAtDesc' || data.order === 'updatedAtDesc'
          ? 'DESC'
          : undefined;

    onQueryFilters({
      name: data.name || undefined,
      orderBy,
      orderDirection,
    });
  });

  useEffect(() => {
    if (debouncedName || order) {
      submitRef.current?.click();
    }
  }, [debouncedName, order]);

  return (
    <Card>
      <CardContent className="p-6">
        <form
          className="flex flex-col space-y-4 md:flex-row md:items-center md:space-x-4 md:space-y-0"
          onSubmit={onSubmit}
        >
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />

              <Input
                placeholder={t('searchPlaceholder')}
                className="pl-10"
                {...register('name')}
              />
            </div>
          </div>

          <Controller
            control={control}
            name="order"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full md:w-[180px]">
                  <SortAsc className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Ordenar por" />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value="createdAtAsc">
                    {t('createdAtAsc')}
                  </SelectItem>

                  <SelectItem value="createdAtDesc">
                    {t('createdAtDesc')}
                  </SelectItem>

                  <SelectItem value="updatedAtAsc">
                    {t('updatedAtAsc')}
                  </SelectItem>

                  <SelectItem value="updatedAtDesc">
                    {t('updatedAtDesc')}
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          />

          <Button ref={submitRef} type="submit" className="hidden" />

          <Button type="button" onClick={handleClearFilters}>
            {t('clearFiltersButtonLabel')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
